import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import { newIsMobile } from '../../helpers/isMobile'
import { useDebounce } from '../../hooks/useDebounce'
import store from '../../store'
import { setInvestmentCollectionFilter } from '../../store/investmentCollection/actions'
import { AppState } from '../../store/rootReducer'
import CloseIcon from '../icons/CloseIcon'
import SettingsIcon from '../icons/SettingsIcon'
import { InvestmentCollectionType } from '../InvestmentCollection/InvestmentCollection'
import styles from './InvestmentFilter.module.scss'
import {
  CurrentValues,
  FilterObjectType,
  StartSliderValue,
} from './InvestmentFilter.types'
import InvestmentFilterDropdown, {
  cityFilterName,
  dropdownData,
} from './InvestmentFilterDropdown'
import InvestmentFilterRooms from './InvestmentFilterRooms'
import InvestmentFilterSliders from './InvestmentFilterSliders'

const nameEventReset = 'resetInvestmentFilter'
const eventReset = new Event(nameEventReset)

export const startValue = {
  slider: {
    cash: 5000000,
    credit: 1000000,
  },
  rooms: 'studio',
  dropdown: {
    city: dropdownData.city[0],
    region: dropdownData.region[dropdownData.city[0].value][0],
  },
  filterValue: {
    region: '',
    city: dropdownData.city[0].label,
    ['selected-city']: dropdownData.city[0].value,
    ['price-to']: '0',
  },
  city: 'Москва',
}

export function createGetParamsFromObject(
  obj: FilterObjectType,
  fixType: boolean
): string {
  const result: string[] = []
  for (const key in obj) {
    if (fixType && key === 'type') {
      continue
    }
    result.push(`${key}=${obj[key]}`)
  }
  return result.length > 0 ? '?' + result.join('&') : ''
}

interface Props {
  type: InvestmentCollectionType
}

const InvestmentFilter: React.FC<Props> = ({ type }) => {
  const [isvision, setIsVision] = useState<boolean>(false)
  const [resetCount, setResetCount] = useState<number>(0)
  const [storeUpdCounter, setStoreUpdCounter] = useState<number>(0)
  const [filterParams, setFilterParams] = useState<FilterObjectType>(
    startValue.filterValue
  )
  const [roomsStartValue, setroomsStartValue] = useState<string>(
    startValue.rooms
  )
  const [startValueSlider, setStartValueSlider] = useState<StartSliderValue>(
    startValue.slider
  )
  const [currentValuesDropdown, setCurrentValuesDropdown] =
    useState<CurrentValues>(startValue.dropdown)

  const fixType = useSelector(
    (state: AppState) => state.investmentCollection[type].fixType
  )

  const popupHandler = () => {
    document.body.style.overflow = isvision ? 'visible' : 'hidden'
    setIsVision(!isvision)
  }

  const updateStore = () => {
    store.dispatch(
      setInvestmentCollectionFilter(
        createGetParamsFromObject(filterParams, fixType),
        type,
        filterParams['selected-city']
      )
    )
  }

  const updCurentValuesDropdown = (value: string, name: string) => {
    if (name !== cityFilterName && name !== 'region') return

    setCurrentValuesDropdown(prevState => {
      if (name === cityFilterName) {
        // only if the city has been changed
        return {
          city: {
            label: value === 'moscow' ? 'Москва' : 'Санкт-Петербург',
            value,
          },
          region: dropdownData.region[value][0],
        }
      } else {
        // only if the region has been changed
        const regionIndex = dropdownData.region[prevState.city.value].findIndex(
          item => item.value === value
        )

        const label =
          dropdownData.region[prevState.city.value][regionIndex].label

        return {
          city: prevState.city,
          region: { label: value === 'Санкт-Петербург' ? 'Все' : label, value },
        }
      }
    })
  }

  const updRoomsValue = (value: string, name: string) => {
    if (name !== 'type') return

    setFilterParams(prevState => ({ ...prevState, rooms: value }))
  }

  // подстановка значения для города, если потребуется
  const updateCityValue = (value: string, name: string) => {
    if (name !== cityFilterName) return

    setFilterParams(prevState => ({
      ...prevState,
      city: dropdownData.city[
        dropdownData.city.findIndex(item => item.value === value)
      ].label,
    }))
  }

  const filterHandler = (value: string, name: string, checked?: boolean) => {
    updCurentValuesDropdown(value, name)
    updRoomsValue(value, name)
    updateCityValue(value, name)

    if (checked === undefined) {
      setFilterParams(prevState => ({ ...prevState, [name]: value }))
    } else {
      setFilterParams(prevState => {
        let result = (prevState[name] || '').split(',')
        if (checked) {
          result.push(value)
        } else {
          result = result.filter(el => el !== value)
        }
        return { ...prevState, [name]: result.join(',') }
      })
    }
  }

  const filterHandlerwithUpdate = (value: string, name: string) => {
    if (newIsMobile()) {
      filterHandler(value, name)

      setStoreUpdCounter(prev => ++prev)
    }
  }

  const resetFilter = () => {
    setroomsStartValue(() => startValue.rooms)
    setCurrentValuesDropdown(() => startValue.dropdown)
    setStartValueSlider(() => startValue.slider)

    setFilterParams(() => startValue.filterValue)

    document.dispatchEvent(eventReset)
    setResetCount(prev => ++prev)
    setStoreUpdCounter(prev => ++prev)
  }

  const applyFilter = () => {
    setStoreUpdCounter(prev => ++prev)

    if (newIsMobile()) {
      popupHandler()
    }
  }

  const debouncestoreUpdCounter = useDebounce<number>(storeUpdCounter, 200)

  useEffect(() => {
    updateStore()
  }, [debouncestoreUpdCounter])

  return (
    <>
      <div
        className={`for-devices ${styles['filter-head']} ${styles['filter-head--mob']}`}
      >
        <div className={styles['filter-row']}>
          <InvestmentFilterDropdown
            filterHandler={filterHandlerwithUpdate}
            currentValues={currentValuesDropdown}
          />
        </div>
        <button
          className='input-wrapper input-wrapper--center'
          onClick={popupHandler}
        >
          Все фильтры
          <SettingsIcon />
        </button>
      </div>
      <div
        className={[
          styles['popup-wrapper'],
          isvision && styles['is-show'],
        ].join(' ')}
      >
        <div className={`for-devices ${styles['popup-head']}`}>
          <h3>Фильтр</h3>
          <button onClick={popupHandler}>
            <CloseIcon />
          </button>
        </div>
        <div className={styles.wrapper}>
          <div className={styles['filter-head']}>
            <div className={styles['filter-row']}>
              <InvestmentFilterDropdown
                filterHandler={filterHandler}
                currentValues={currentValuesDropdown}
              />
            </div>
          </div>
          <InvestmentFilterRooms
            filterHandler={filterHandler}
            startValue={roomsStartValue}
            nameEventReset={nameEventReset}
          />
          <InvestmentFilterSliders
            filterHandler={filterHandler}
            startValue={startValueSlider}
            nameEventReset={nameEventReset}
            resetCount={resetCount}
          />{' '}
          <div className={styles['btn-group']}>
            <button className={`btn ${styles.btn}`} onClick={applyFilter}>
              Смотреть
            </button>
            <button
              className={`btn btn--gray for-devices ${styles.btn}`}
              onClick={resetFilter}
            >
              Сбросить
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

export default InvestmentFilter
